import { EdhConditionDisclosureResource } from "../types/EdhConditionDisclosureResource";
import { QuestionPageResource } from "../types/QuestionPageResource";
import { QuestionSectionResource } from "../types/QuestionSectionResource";
import merge from "lodash.merge";

/**
 * Updates the global disclosures in state.
 * @param receivedDisclosures Updated disclosures received from saga.
 * @param embeddedDisclosures Current embedded disclosures in state.
 * @return An updated global disclosures object.
 */
export const mergeUpdatedDisclosures = (
  receivedDisclosures: EdhConditionDisclosureResource[],
  embeddedDisclosures?: EdhConditionDisclosureResource[],
) => {
  const updatedDisclosures = embeddedDisclosures ? embeddedDisclosures : [];

  // Go through the response from API.
  receivedDisclosures.forEach(
    (receivedDisclosure: EdhConditionDisclosureResource) => {
      // If a disclosure is found, update it
      if (
        updatedDisclosures.some(
          (disclosure) => disclosure.aliasName === receivedDisclosure.aliasName,
        )
      ) {
        return updateDisclosures(updatedDisclosures, receivedDisclosure);
      }
      // If no matching disclosure found, add disclosure to list.
      return updatedDisclosures.push(receivedDisclosure);
    },
  );
  return updatedDisclosures;
};

function updateDisclosures(
  updatedDisclosures: EdhConditionDisclosureResource[],
  receivedDisclosure: EdhConditionDisclosureResource,
) {
  // If matching disclosure found, update disclosure.
  return updatedDisclosures.forEach(
    (disclosure: EdhConditionDisclosureResource, index: number) => {
      /* Before merging the disclosure, Ensure we dont persist
         old answer values for updated questions. */
      if (disclosure.aliasName === receivedDisclosure.aliasName) {
        if (disclosure.questions) {
          resetAnswerValue(disclosure, receivedDisclosure);
        }

        // Merge the rest of the properties (links etc.)
        const mergedDisclosure = merge(disclosure, receivedDisclosure);
        updatedDisclosures[index] = mergedDisclosure;

        // If links are banked into all responses in the future, use this approach
        // updatedDisclosures[index] = receivedDisclosure;
        // Discard now irrelevant questions.
        if (updatedDisclosures[index].questions) {
          updatedDisclosures[index].questions = updatedDisclosures[
            index
          ].questions.slice(0, receivedDisclosure.questions.length);
        }
      }
    },
  );
}
function resetAnswerValue(
  disclosure: EdhConditionDisclosureResource,
  receivedDisclosure: EdhConditionDisclosureResource,
) {
  disclosure.questions = disclosure.questions.map((question, questionIndex) => {
    if (
      receivedDisclosure.questions[questionIndex] &&
      !receivedDisclosure.questions[questionIndex].answerValue
    ) {
      question.answerValue = "";
    }

    return question;
  });
}

export const removeEmbeddedDisclosureByAliasName = (
  aliasName: string,
  embeddedDisclosures?: EdhConditionDisclosureResource[],
) => {
  return (
    embeddedDisclosures &&
    embeddedDisclosures.filter(
      (disclosure) => disclosure.aliasName !== aliasName,
    )
  );
};

export const removeDisclosureFromBaseQuestionByAliasName = (
  baseQuestionCode: string,
  aliasName: string,
  pages: QuestionPageResource[],
) => {
  return pages.map((page) => {
    return {
      ...page,
      sections: page.sections.map((section: QuestionSectionResource) => {
        return {
          ...section,
          baseQuestions: section.baseQuestions.map((baseQuestion) => {
            const { code } = baseQuestion;

            if (code === baseQuestionCode) {
              // Remove from _embedded on base question
              if (
                baseQuestion._embedded &&
                baseQuestion._embedded.disclosures
              ) {
                baseQuestion._embedded.disclosures =
                  baseQuestion._embedded?.disclosures.filter(
                    (disclosure: EdhConditionDisclosureResource) => {
                      return disclosure.aliasName !== aliasName;
                    },
                  );
              }

              // Remove from disclosures array on base question
              if (baseQuestion.disclosures) {
                baseQuestion.disclosures = baseQuestion.disclosures?.filter(
                  (disclosure) => {
                    return disclosure.aliasName !== aliasName;
                  },
                );
              }

              if (
                baseQuestion.disclosures &&
                baseQuestion.disclosures.length === 0
              ) {
                baseQuestion.disclosures = undefined;
              }

              if (
                baseQuestion._embedded &&
                baseQuestion._embedded.disclosures &&
                baseQuestion._embedded.disclosures.length === 0
              ) {
                baseQuestion._embedded.disclosures = undefined;
              }
            }
            return baseQuestion;
          }),
        };
      }),
    };
  });
};

/**
 * Function to update disclosures in base question object.
 * NOTE: Not currently in use as it is somewhat redunant, and triggers unwanted
 * scrolling behavior when used.
 *
 * @param pages List if interview pages.
 * @param disclosure An updated disclosure.
 * @returns List of pages with disclosure merged into state.
 */
export const mergeUpdatedDisclosureIntoBaseQuestion = (
  pages: QuestionPageResource[],
  disclosure: EdhConditionDisclosureResource,
) => {
  const { baseQuestionCode } = disclosure.baseQuestionsDisclosures[0];
  const { aliasName } = disclosure;

  return pages.map((page) => {
    return {
      ...page,
      sections: page.sections.map((section: QuestionSectionResource) => {
        return {
          ...section,
          baseQuestions: section.baseQuestions.map((baseQuestion) => {
            const { code } = baseQuestion;

            if (code === baseQuestionCode) {
              if (
                baseQuestion._embedded &&
                baseQuestion._embedded.disclosures
              ) {
                baseQuestion._embedded.disclosures =
                  baseQuestion._embedded?.disclosures.map(
                    (embeddedDisclosure: EdhConditionDisclosureResource) => {
                      if (embeddedDisclosure.aliasName === aliasName) {
                        return merge(embeddedDisclosure, disclosure);
                      }
                      return embeddedDisclosure;
                    },
                  );
              }

              if (baseQuestion.disclosures) {
                baseQuestion.disclosures = baseQuestion.disclosures?.map(
                  (d) => {
                    if (d.aliasName === aliasName) {
                      return merge(d, disclosure);
                    }
                    return d;
                  },
                );
              }
            }
            return baseQuestion;
          }),
        };
      }),
    };
  });
};
